"use client";

import { useApolloClient } from "@apollo/client/react";
import { AlertCircle, Check, Loader2, RefreshCw, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { ALLOWED_UPLOAD_MIMES, fileToBase64, MAX_UPLOAD_BYTES } from "@/lib/file-to-base64";
import { useUploadVisitorMediaMutation } from "@/lib/graphql/queries/generated/messaging.generated";
import {
  useUpdateVisitorProfileMutation,
  useVisitorProfileQuery,
  type VisitorProfile,
  VisitorProfileDocument,
  type VisitorProfileField,
  type VisitorProfileFieldUpdate,
} from "@/lib/graphql/queries/generated/profile.generated";
import { useOutboxContext } from "@/lib/outbox-context";
import { usePreview } from "@/lib/preview/use-preview";
import { setDemoPref, useDemoPrefs } from "@/lib/runtime-demo-prefs";
import { useSessionContentContext } from "@/lib/targeting/session-content-context";
import type { ThemePreference } from "@/lib/use-theme-mode";
import { useWidgetConfig } from "@/lib/use-widget-config";
import { useWidgetEnv } from "@/lib/use-widget-env";
import { cn } from "@/lib/utils";
import { AvatarHero } from "./avatar-hero";
import { FieldRow } from "./field-row";
import { diffFields, isEmpty, pickDisplayName, seedValues } from "./helpers";
import { PreferencesSection } from "./preferences-section";
import { withPreviewFields } from "./preview-fields";
import {
  extractInvalidFieldKey,
  extractProfileErrorKind,
  profileErrorI18nKey,
} from "./profile-error";
import { applySavedFields, reconcileAfterSave } from "./save-reconcile";
import { Section } from "./section";
import { useApplyPref } from "./use-apply-pref";
import { ensurePhoneValidation, validateFieldValue } from "./validators";

interface ProfileTabProps {
  /** Demo mode — the visitor-profile query is BE-only; in demo we render
   *  an explanatory placeholder so the tab still has *something* to show. */
  isDummy: boolean;
  /** The session's first handshake is still in flight: show the skeleton and do not query yet. */
  pending?: boolean;
}

/**
 * Visitor-editable profile + preferences.
 *
 * Field shape + types are dictated by the BE (`visitorProfile.fields[]`).
 * Identity fields (name, email, phone, …) buffer locally and write on
 * "Save"; preferences (theme, language, notifications) auto-save with
 * an optimistic cache write so the widget chrome flips this tick.
 *
 * AVATAR is rendered as a centered hero, uploadable via the visitor
 * media service (base64 → `uploadVisitorMedia`); the resulting URL is
 * the value stored back on the profile field.
 *
 * This file is the ORCHESTRATOR — every concern lives in a sibling
 * module so each piece is independently testable:
 *
 *   - `avatar-hero.tsx`        — the centered avatar widget
 *   - `field-row.tsx`          — one row in the identity form
 *   - `field-controls/`        — one file per `VisitorProfileFieldType`
 *   - `preferences-section.tsx` — language / theme / notifications
 *   - `use-apply-pref.ts`      — preference auto-save hook
 *   - `helpers.ts`             — seed / diff / normalise / display
 *   - `validators.ts`          — email + phone (libphonenumber-js/min)
 *   - `profile-error.ts`       — Apollo error → kind + field key
 */
export function ProfileTab({ isDummy, pending = false }: ProfileTabProps) {
  const t = useTranslations("profile");
  const locale = useLocale();
  const { config } = useWidgetConfig();
  const env = useWidgetEnv();
  // Custom profile fields seeded into the demo profile (playground preview).
  const demoProfileFields = env.demoProfileFields;
  // Dashboard preview only (a token or an audience override): draft custom fields, read-only.
  const { token: previewToken } = usePreview();
  const inPreview = !!previewToken || !!env.audienceOverride;
  const previewExtra = inPreview ? env.previewProfileFields : undefined;

  // Profile-tab language picker is ALWAYS visible — visitor's saved choice
  // overrides the operator's `localeStrategy` (the cookie set on save sits
  // above strategy in `detectClientLocale`'s precedence chain), so visitors
  // can always switch their own UI language even when the operator pinned
  // a default via `DEFAULT` strategy.

  const {
    data,
    loading,
    error: loadError,
    refetch,
  } = useVisitorProfileQuery({ skip: isDummy || pending });
  // Demo mode → synthesise a profile so the tab renders the full UI
  // (avatar hero + four common identity fields + preferences) instead
  // of a title-only placeholder. Memoised because several downstream
  // effects depend on `profile` identity (`useEffect([profile])` →
  // `setValues(seedValues(profile))`). Without memoisation a fresh
  // demo object on every render restarts that effect each tick and
  // triggers React error #185 (Maximum update depth exceeded). Save
  // mutations short-circuit on `isDummy` deeper down so editing
  // values is harmless in demo.
  // Fields the visitor "saved" in demo (no BE to write to): applied on top of the synthetic profile
  // so the form's source of truth holds them and the seed effect does not snap back to the old value.
  const [demoSaved, setDemoSaved] = useState<VisitorProfileFieldUpdate[]>([]);
  const demoProfile = useMemo(
    () =>
      isDummy
        ? applySavedFields(buildDemoVisitorProfile(locale, demoProfileFields), demoSaved)
        : null,
    [isDummy, locale, demoProfileFields, demoSaved],
  );
  // Demo preference overrides — live in the global `runtime-demo-prefs`
  // bus so changes propagate widget-wide (theme flip, language switch,
  // notification opt-in) instead of just inside this tab's local state.
  // Live mode reads/writes straight to Apollo cache via mutations.
  const demoPrefsSnapshot = useDemoPrefs();
  const profile: VisitorProfile | undefined = useMemo(() => {
    if (demoProfile) {
      return {
        ...demoProfile,
        preferences: {
          ...demoProfile.preferences,
          ...(demoPrefsSnapshot.themeMode != null && {
            themeMode: demoPrefsSnapshot.themeMode,
          }),
          ...(demoPrefsSnapshot.language != null && {
            language: demoPrefsSnapshot.language,
          }),
          desktopNotifications: demoPrefsSnapshot.desktopNotifications,
        },
      };
    }
    const live = data?.visitorProfile;
    return live && previewExtra ? withPreviewFields(live, previewExtra) : live;
  }, [demoProfile, demoPrefsSnapshot, data?.visitorProfile, previewExtra]);

  const [update, { loading: saving }] = useUpdateVisitorProfileMutation();
  // A saved profile changes `contact.*` in the session content (contract v1).
  const { refresh: refreshSessionContent } = useSessionContentContext();
  const client = useApolloClient();
  const outbox = useOutboxContext();
  const [uploadMedia, { loading: avatarUploading }] = useUploadVisitorMediaMutation();
  const applyDemoPref = useCallback(
    (key: "language" | "desktopNotifications" | "themeMode", value: string | boolean | null) => {
      if (key === "language") setDemoPref("language", typeof value === "string" ? value : null);
      else if (key === "themeMode") setDemoPref("themeMode", normaliseDemoTheme(value));
      else if (key === "desktopNotifications") setDemoPref("desktopNotifications", !!value);
    },
    [],
  );
  const {
    apply: applyPref,
    saving: prefSaving,
    error: prefError,
  } = useApplyPref(profile, t, isDummy ? applyDemoPref : undefined);

  // Identity-field draft buffer. Edits ONLY become part of the mutation
  // when the visitor taps "Save" — preferences are auto-saved separately
  // (see `useApplyPref`). Keeping the two paths apart means a half-typed
  // name never sneaks into a theme-flip's payload.
  const [values, setValues] = useState<Record<string, string | null>>({});
  const [hasEdits, setHasEdits] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Latest form values, readable after an `await` (the save handler's closure holds the snapshot
  // taken when Save was pressed).
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Warm the lazy phone validator so Save does not wait for it.
  useEffect(() => {
    void ensurePhoneValidation();
  }, []);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );
  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setSavedFlash(false), 2400);
  }, []);
  // A queued profile / preference write the server permanently rejected (see the shell's
  // `onDropped`): the optimistic value was rolled back, tell the visitor. Only drops that happen
  // while this tab is mounted count; an older one is not news.
  const mountedAt = useRef(Date.now());
  const dropped = outbox?.dropped;
  useEffect(() => {
    if (dropped && dropped.at > mountedAt.current) setSubmitError(t("errorGeneric"));
  }, [dropped, t]);

  // Seed (or re-seed after a save) when the BE-resolved profile changes
  // and the visitor isn't mid-edit. Preferences are NOT seeded — they
  // read straight off `profile.preferences` (optimistic cache writes
  // flip the cache, so the form always reflects authoritative state
  // without local buffering).
  useEffect(() => {
    if (!profile) return;
    if (hasEdits) return;
    setValues(seedValues(profile));
  }, [profile, hasEdits]);

  // Operator-curated field ordering (live-preview surface from the
  // dashboard's Profile sub-tab). When present, we filter the
  // BE-returned `regularFields` to this set AND render them in the
  // operator's order. `null` / unset ⇒ render every field the BE
  // returned in BE order — current behaviour for integrations the
  // operator hasn't customised.
  //
  // Caveat: this only re-orders/filters fields the BE already returned.
  // A brand-new field that isn't in the BE allow-list yet won't appear
  // live; it shows up after Save → BE refetch. If we ever want adds to
  // reflect live too, the dashboard can inject a
  // `WidgetEnv.previewData.visitorProfile` (same pattern as
  // helpArticles) and the profile-tab can use it as a fallback.
  const profileOrderKeys = config.messenger?.profile?.fields ?? null;
  const { avatarField, regularFields } = useMemo(() => {
    if (!profile) return { avatarField: null as VisitorProfileField | null, regularFields: [] };
    const av = profile.fields.find((f) => f.type === "AVATAR") ?? null;
    const rest = profile.fields.filter((f) => f.type !== "AVATAR");
    if (!profileOrderKeys) return { avatarField: av, regularFields: rest };
    const byKey = new Map(rest.map((f) => [f.key, f]));
    const ordered = profileOrderKeys
      .map((k) => byKey.get(k))
      .filter((f): f is VisitorProfileField => !!f);
    // Draft preview fields are not in the saved allow-list yet: they follow the ordered fields.
    if (previewExtra) {
      const draftKeys = new Set(previewExtra.map((f) => f.key));
      for (const f of rest) {
        if (f.pinned && draftKeys.has(f.key) && !ordered.includes(f)) ordered.push(f);
      }
    }
    // The avatar is part of the operator's field allow-list too — hide it when
    // the config omits its key (mirrors how the regular fields are filtered).
    const avatarShown = av && profileOrderKeys.includes(av.key) ? av : null;
    return { avatarField: avatarShown, regularFields: ordered };
  }, [profile, profileOrderKeys, previewExtra]);

  const setFieldValue = useCallback((key: string, value: string | null) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setHasEdits(true);
    setSavedFlash(false);
    setSubmitError(null);
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleDiscard = useCallback(() => {
    if (!profile) return;
    setValues(seedValues(profile));
    setHasEdits(false);
    setSubmitError(null);
    setFieldErrors({});
  }, [profile]);

  const handleSave = useCallback(async () => {
    if (!profile) return;
    await ensurePhoneValidation();

    // Client-side validation: required + per-field (email/phone) checks.
    // Collect ALL errors before bailing so the visitor sees every bad
    // field at once instead of one-at-a-time whack-a-mole.
    const nextErrors: Record<string, string> = {};
    for (const f of profile.fields) {
      if (f.pinned) continue;
      const v = values[f.key] ?? null;
      if (f.required && isEmpty(v)) {
        nextErrors[f.key] = t("requiredHint");
        continue;
      }
      const kind = validateFieldValue(f, v);
      if (kind) nextErrors[f.key] = t(kind);
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setSubmitError(t("errorFieldInvalid"));
      return;
    }

    const fieldUpdates = diffFields(profile.fields, values);
    if (fieldUpdates.length === 0) return;

    setSubmitError(null);
    setFieldErrors({});
    // Demo mode — no BE to write to. Simulate a successful save:
    // commit the draft values into the seed, clear the edit flag, and
    // flash the "saved" chip. Avatar uploads are similarly handled in
    // `handleAvatarFile` below (the `uploadVisitorMedia` mutation is
    // also skipped in demo).
    // The form reflects `saved` afterwards, except for text typed while the save was in flight.
    const finishSave = (saved: VisitorProfile) => {
      const next = reconcileAfterSave({
        sent: values,
        current: valuesRef.current,
        saved: seedValues(saved),
      });
      setValues(next.values);
      setHasEdits(next.hasEdits);
      flashSaved();
    };
    if (isDummy) {
      setDemoSaved((prev) => [
        ...prev.filter((p) => !fieldUpdates.some((u) => u.key === p.key)),
        ...fieldUpdates,
      ]);
      finishSave(applySavedFields(profile, fieldUpdates));
      return;
    }
    // Offline outbox path — client validation already passed, so queue a
    // CONVERGENCE profile-save (retried on reconnect) and treat it as saved
    // optimistically instead of erroring. Online (incl. BE-down-but-online) we
    // keep the direct path so server-side field validation still surfaces.
    if (outbox && !outbox.online) {
      outbox.enqueue({
        op: "setProfile",
        semantics: "convergence",
        convergenceKey: "profile",
        payload: { fields: fieldUpdates },
      });
      // Applied locally first: the cache is what the form re-seeds from, so it must hold the new
      // values or the form snaps back to the old ones while claiming "saved".
      const saved = applySavedFields(profile, fieldUpdates);
      client.writeQuery({ query: VisitorProfileDocument, data: { visitorProfile: saved } });
      finishSave(saved);
      return;
    }
    try {
      const res = await update({
        variables: { input: { fields: fieldUpdates } },
        update: (cache, { data: mut }) => {
          const next = mut?.updateVisitorProfile;
          if (!next) return;
          cache.writeQuery({
            query: VisitorProfileDocument,
            data: { visitorProfile: next },
          });
        },
      });
      const next = res.data?.updateVisitorProfile;
      if (next) {
        finishSave(next);
        refreshSessionContent();
      }
    } catch (err) {
      const kind = extractProfileErrorKind(err);
      if (kind === "fieldInvalid") {
        const fk = extractInvalidFieldKey(err);
        if (fk) setFieldErrors({ [fk]: t("errorFieldInvalid") });
      }
      setSubmitError(t(profileErrorI18nKey(kind)));
    }
  }, [profile, t, update, values, isDummy, outbox, client, flashSaved, refreshSessionContent]);

  const handleAvatarFile = useCallback(
    async (file: File) => {
      if (!avatarField) return;
      setSubmitError(null);
      if (!ALLOWED_UPLOAD_MIMES.has(file.type) || !file.type.startsWith("image/")) {
        setSubmitError(t("errorGeneric"));
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setSubmitError(t("errorGeneric"));
        return;
      }
      try {
        // Demo mode — read the dropped image as a data: URL so the
        // avatar hero previews it locally, no BE upload. The avatar
        // doesn't persist past the session (the demo profile is rebuilt
        // from scratch on next mount).
        if (isDummy) {
          const { base64 } = await fileToBase64(file);
          setFieldValue(avatarField.key, `data:${file.type};base64,${base64}`);
          return;
        }
        const { base64 } = await fileToBase64(file);
        const res = await uploadMedia({
          variables: {
            input: { filename: file.name, mimetype: file.type, dataBase64: base64 },
          },
        });
        const url = res.data?.uploadVisitorMedia?.url;
        if (!url) throw new Error("empty upload response");
        setFieldValue(avatarField.key, url);
      } catch {
        setSubmitError(t("errorGeneric"));
      }
    },
    [avatarField, isDummy, setFieldValue, t, uploadMedia],
  );

  if (loading || pending) {
    return (
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-4 px-5 py-6" aria-busy="true">
          <div className="mx-auto flex flex-col items-center gap-3">
            <Skeleton className="h-24 w-24 rounded-full" />
            <Skeleton className="h-4 w-32 rounded" />
          </div>
          <Skeleton className="h-9 w-full rounded-wx-sm" />
          <Skeleton className="h-9 w-full rounded-wx-sm" />
          <Skeleton className="h-9 w-full rounded-wx-sm" />
          <span className="sr-only">{t("loading")}</span>
        </div>
      </ScrollArea>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-wx-danger/10 text-wx-danger">
          <AlertCircle size={24} aria-hidden="true" />
        </div>
        <p className="max-w-xs text-sm text-wx-fg-muted">{t("loadError")}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1.5 rounded-full bg-wx-primary px-4 py-2 text-sm font-medium text-wx-primary-fg transition-colors hover:bg-wx-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary focus-visible:ring-offset-2 focus-visible:ring-offset-wx-bg"
        >
          <RefreshCw size={14} aria-hidden="true" />
          {t("retry")}
        </button>
      </div>
    );
  }

  const avatarValue = avatarField ? (values[avatarField.key] ?? null) : null;
  const avatarHeroName = pickDisplayName(profile, values);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea className="min-h-0 flex-1">
        <form
          id="wx-profile-form"
          // The widget validates (required / email / phone) with localised, in-panel messages; the
          // browser's own bubble is in the browser's language and spills out of the panel.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          className="flex flex-col px-5 pt-2 pb-4"
        >
          {avatarField && (
            <AvatarHero
              field={avatarField}
              value={avatarValue}
              uploading={avatarUploading}
              onPick={handleAvatarFile}
              onClear={() => setFieldValue(avatarField.key, null)}
              displayName={avatarHeroName}
              t={t}
            />
          )}

          {regularFields.length > 0 && (
            <Section eyebrow={t("title")}>
              {regularFields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={values[f.key] ?? null}
                  onChange={(v) => setFieldValue(f.key, v)}
                  error={fieldErrors[f.key]}
                  t={t}
                />
              ))}
            </Section>
          )}

          {!avatarField && regularFields.length === 0 && (
            <p className="py-6 text-center text-sm text-wx-fg-muted">{t("noFields")}</p>
          )}

          <PreferencesSection
            profile={profile}
            locale={locale}
            themeMode={config.themeMode}
            prefSaving={prefSaving}
            applyPref={applyPref}
            onNotificationError={(k) => setSubmitError(k ? t(k) : null)}
            t={t}
          />

          {(submitError ?? prefError) && (
            <p className="mt-4 flex items-start gap-1.5 rounded-wx-sm bg-wx-danger/10 px-3 py-2 text-xs text-wx-danger">
              <X size={13} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>{submitError ?? prefError}</span>
            </p>
          )}
          {savedFlash && !submitError && !prefError && (
            <p className="mt-4 flex items-center gap-1.5 rounded-wx-sm bg-wx-success/10 px-3 py-2 text-xs text-wx-success">
              <Check size={13} aria-hidden="true" />
              <span>{t("saved")}</span>
            </p>
          )}
        </form>
      </ScrollArea>

      {/* Sticky save bar — only shown when identity fields have unsaved
          edits. Preferences auto-save (see `useApplyPref`) and don't
          surface a button here. */}
      {hasEdits && (
        <div className="flex items-center gap-2 border-t border-wx-border bg-wx-bg/95 px-4 py-3 backdrop-blur">
          <button
            type="submit"
            form="wx-profile-form"
            disabled={saving}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-full bg-wx-primary px-4 py-2 text-sm font-semibold text-wx-primary-fg",
              "hover:bg-wx-primary-hover disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            {saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {saving ? t("saving") : t("save")}
          </button>
          <button
            type="button"
            onClick={handleDiscard}
            disabled={saving}
            className={cn(
              "rounded-full bg-wx-bg-elevated px-4 py-2 text-sm font-medium text-wx-fg",
              "hover:bg-wx-bg-elevated-2 disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            {t("cancel")}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Build a synthetic visitor profile for demo mode. Same `VisitorProfile`
 * shape the BE returns, so the rendering code path doesn't need a
 * separate branch for demo data — just `profile = isDummy ? buildDemo… :
 * data?.visitorProfile`. Saving / mutations are no-ops in demo: the
 * Apollo mutation hooks still exist (hook ordering) but the bundled
 * mock content never reaches a `useMutation.mutateFn` call because
 * there's no real BE connection in this mode.
 */
/** Normalise the BE's `system | light | dark` theme value to the
 *  `ThemePreference` shape (`auto | light | dark`) the demo bus stores.
 *  Accepts the legacy uppercase form for resilience. */
function normaliseDemoTheme(value: string | boolean | null): ThemePreference | null {
  if (typeof value !== "string") return null;
  const v = value.toLowerCase();
  if (v === "light" || v === "dark") return v;
  if (v === "auto" || v === "system") return "auto";
  return null;
}

function buildDemoVisitorProfile(
  locale: string,
  customFields: { key: string; label: string }[] = [],
): VisitorProfile {
  // Labels hardcoded — in live mode they come from the BE
  // (operator-configured). The DEMO is primarily for sales / marketing
  // previews where English defaults read fine; adding per-locale field
  // names would require dedicated i18n keys + translation across 33
  // locales for content that's never actually saved.
  return {
    themeModeEditable: true,
    preferences: {
      language: locale,
      themeMode: "system",
      desktopNotifications: false,
    },
    fields: [
      {
        key: "avatar",
        label: "Avatar",
        type: "AVATAR",
        required: false,
        pinned: false,
        value: null,
      },
      {
        key: "name",
        label: "Name",
        type: "STRING",
        required: false,
        pinned: false,
        value: "John Doe",
      },
      { key: "email", label: "Email", type: "STRING", required: false, pinned: false, value: "" },
      { key: "phone", label: "Phone", type: "STRING", required: false, pinned: false, value: "" },
      // Operator-added custom fields (playground preview) — rendered as empty
      // open-text fields alongside the built-ins.
      ...customFields.map((f) => ({
        key: f.key,
        label: f.label || f.key,
        type: "STRING" as const,
        required: false,
        pinned: false,
        value: "",
      })),
    ],
  };
}
